// First: takes a verify link's code off the address bar before the router or analytics read it.
import './utils/verifyLinkCode'
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { loadAccount, restoreSignIn, startSession } from './app/routes'
import { watchStripeReturn } from './app/pendingBooking'
import * as Klaro from 'klaro'
import 'klaro/dist/klaro.css'
import './assets/klaro-theme.css'
import klaroConfig from './klaro-config'
import { trackCookieNotice } from './app/cookieNotice'
import { afterPaint } from './utils/afterPaint'
import srcAfterInsert from './utils/srcAfterInsert'

// Restore this tab's sign-in, or sign out a log-in whose browser session has ended
// (restoreSignIn in routes.js). Must run before .use(router) starts the first navigation.
// The member pages that show the account (the booking page, My PODs, /checkout). On a
// full load of one, its account starts loading here, while the page's code downloads.
const ACCOUNT_PAGE = /^\/(book\/[^/]+\/[^/]+|mypods|checkout)\/?$/i
let signedIn = false
if (typeof window !== 'undefined' && !window.__PRERENDER__) {
  signedIn = restoreSignIn()
  if (signedIn && ACCOUNT_PAGE.test(window.location.pathname)) loadAccount()
}

// Back from Stripe with a saved booking: on to its booking page (pendingBooking.js).
// Registered before .use(router) starts the first navigation.
watchStripeReturn(router)

// Mount only once the initial route is ready (its lazy chunk loaded and
// beforeEach resolved). Mounting clears #app, so mounting earlier wiped the
// prerendered page and left an empty <router-view> until the route chunk
// arrived: a 0.7-1.1 s blank on every lazy route on a throttled phone. Now
// the prerendered page stays on screen until Vue can render the real one.
// If the first navigation fails, still mount, exactly as before. (Two-argument
// then: an error thrown by mount itself isn't caught and retried.)
//
// History: we also tried createSSRApp() to hydrate the prerendered DOM in
// place. It didn't work because the Puppeteer snapshot is client-rendered
// DOM without SSR hydration markers, and hydration appended a second tree
// (every route rendered twice, CLS 1.0). Plain createApp() replaces the
// prerendered DOM cleanly, which is what we keep here. Re-evaluate with a
// real SSR pipeline (@vue/server-renderer).
const app = createApp(App).use(router)
app.directive('src-after-insert', srcAfterInsert)
router.isReady().then(() => app.mount('#app'), () => app.mount('#app'))

// Signed-in members: record the visit and check the token in the background (never
// awaited). At every load when signed in, once the first navigation is done, so the
// router has already stored an ad click from this landing. Again whenever the tab
// comes back into view, signed in at load or not (they may log in on this page);
// startSession then calls only if 30+ minutes have passed since this tab's last
// check-in, and returns early with no token. If the token is dead, startSession
// signs out; a page that needs sign-in then goes to the log-in form, since this
// visitor had an account.
if (typeof window !== 'undefined' && !window.__PRERENDER__) {
  const checkSession = (atLoad) => startSession({ atLoad }).then((result) => {
    if (result !== 'signed-out') return
    if (router.currentRoute.value.matched.some(record => record.meta.requiresAuth)) {
      router.replace({ name: 'join', query: { show: 'login' } })
    }
  }, () => { /* never sign out on an unexpected error */ })
  if (signedIn) {
    const atLoad = () => checkSession(true)
    router.isReady().then(atLoad, atLoad)
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkSession(false)
  })
}

// Klaro cookie consent + Consent Mode v2 update wiring.
// Skipped during the Puppeteer prerender pass (window.__PRERENDER__ is
// set by scripts/prerender.js:177 via page.evaluateOnNewDocument) so
// the modal DOM is not baked into the saved HTML for every route. The
// 300 ms delay lets the LCP element paint before the modal renders —
// intentional, do not remove. Klaro reads consent state from the
// `gympods-consent` cookie set in klaro-config.js and applies it.
// After every applyConsents pass, sendConsent() pushes one
// gtag('consent', 'update', ...) with all four signals, then a plain
// {event: 'consent_update', ...} object so GTM triggers (which only
// fire on Custom Events, not on the `consent` gtag command) can re-fire
// tags gated on consent. GTM (loaded in bootAnalytics() in
// public/index.html) sees the consent update and permits/blocks each
// tag accordingly; consent_update always lands after it, never before.
//
// Klaro runs applyConsents() inside the manager constructor, before we
// can attach the watcher, so the state at setup (the stored choice, or
// all denied for a new visitor) is sent explicitly. A choice made on
// the banner or in the settings is sent after the tap has painted
// (afterPaint): with GTM loaded, the Google tag handles a consent
// update synchronously, which made "Accept all" slow to respond.
if (typeof window !== 'undefined' && !window.__PRERENDER__) {
  setTimeout(() => {
    Klaro.setup(klaroConfig)
    window.klaro = Klaro
    trackCookieNotice()

    const manager = Klaro.getManager(klaroConfig)

    // What Klaro has just applied, in the two forms GTM reads. Consent Mode:
    // each purpose takes the state of its last service (Clarity, Google Ads),
    // exactly as when every service sent its own update from onAccept/onDecline.
    // consent_update: granted when any service in the purpose is on, as before.
    function readConsent() {
      let analyticsMode = 'denied'
      let adMode = 'denied'
      let adGranted = false
      let analyticsGranted = false
      for (const service of klaroConfig.services) {
        const purposes = service.purposes || []
        // manager.states: what applyConsents applied to each service (klaro/src/consent-manager.js).
        const applied = manager.states[service.name] ? 'granted' : 'denied'
        if (purposes.includes('analytics')) analyticsMode = applied
        if (purposes.includes('marketing')) adMode = applied
        if (!manager.getConsent(service.name)) continue
        if (purposes.includes('analytics')) analyticsGranted = true
        if (purposes.includes('marketing')) adGranted = true
      }
      return {
        mode: { analytics_storage: analyticsMode, ad_storage: adMode, ad_user_data: adMode, ad_personalization: adMode },
        event: {
          event: 'consent_update',
          consent_ad_storage: adGranted ? 'granted' : 'denied',
          consent_analytics_storage: analyticsGranted ? 'granted' : 'denied'
        }
      }
    }

    function sendConsent(consent, withEvent) {
      window.gtag && window.gtag('consent', 'update', consent.mode)
      if (!withEvent) return
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(consent.event)
    }

    sendConsent(readConsent(), manager.confirmed)

    manager.watch({
      update(_m, type) {
        if (type !== 'applyConsents') return
        const consent = readConsent()
        afterPaint(() => sendConsent(consent, true))
      }
    })
  }, 300)
}

// Core Web Vitals from real visits, to the dataLayer as web_vitals (GTM sends
// them to GA4). Loaded once the page is idle after load, so start-up doesn't
// pay for it. A direct push, not afterPaint: the final values are reported as
// the page is hidden, when no frame is painted.
if (typeof window !== 'undefined' && !window.__PRERENDER__) {
  const startWebVitals = () => {
    import(/* webpackChunkName: "web-vitals" */ 'web-vitals/attribution').then(({ onCLS, onINP, onLCP }) => {
      const report = (metric, extra) => {
        try {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'web_vitals',
            metric_name: metric.name,
            metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
            metric_rating: metric.rating,
            metric_id: metric.id,
            page_path: window.location.pathname,
            ...extra
          })
        } catch (e) { /* analytics never strands the user */ }
      }
      onINP((metric) => {
        const a = metric.attribution
        report(metric, {
          inp_target: (a.interactionTarget || '').slice(0, 100),
          inp_type: a.interactionType,
          inp_load_state: a.loadState,
          inp_input_delay: Math.round(a.inputDelay),
          inp_processing: Math.round(a.processingDuration),
          inp_presentation: Math.round(a.presentationDelay)
        })
      })
      onLCP((metric) => report(metric))
      onCLS((metric) => report(metric))
    }, () => { /* chunk failed to load: no field data this visit */ })
  }
  const whenIdle = () => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(startWebVitals)
    else setTimeout(startWebVitals, 0)
  }
  if (document.readyState === 'complete') whenIdle()
  else window.addEventListener('load', whenIdle, { once: true })
}
